/**
 * Intel at the briefing (04 §8): three cards on offer per mission node, bought with RP and
 * attached to the node, so the generator sees them in `params.intel`. A free card from a
 * Signals Intercept is held by the operation and rides on whichever mission flies next.
 */

import { Pcg32 } from '../sim/rng';
import type { IntelId } from '../sim/types';
import { INTEL, INTEL_OFFERS, type IntelInfo, intelFor } from './content/modifiers';
import { activeOperation, type OperationState, type ProfileState } from './state';
import type { IntelOffer, TheatreNode } from './types';
import { shuffled } from './util';

const flyable = (node: TheatreNode): boolean => node.kind === 'mission' || node.kind === 'rescue';

/** The intel cards on offer at a node's briefing: a pure function of the node's seed. */
export function intelOffers(node: TheatreNode): IntelId[] {
  if (!node.params || !flyable(node)) return [];
  const rng = new Pcg32(node.params.seed, 0x0908);
  // The node's conditions decide too: a card that is only ink on the strip map is worth nothing under
  // Radio Silence, and selling it there would be the same defect this round is about (04 §7, §8).
  const usable = intelFor(node.params.archetype, node.params.modifiers, node.params.threat);
  return shuffled(rng, usable).slice(0, INTEL_OFFERS).map((card) => card.id);
}

function owns(op: OperationState, node: TheatreNode, id: IntelId): boolean {
  return (node.params?.intel.includes(id) ?? false) || op.intelHeld.includes(id);
}

/**
 * What the briefing shows: the node's offers with prices, then any card it already has that
 * was not among them (an intercept's free card), marked `owned`. Empty for a support node.
 */
export function offersFor(op: OperationState, node: TheatreNode): IntelOffer[] {
  if (!node.params || !flyable(node)) return [];
  const onSale = intelOffers(node);
  const extra = [...node.params.intel, ...op.intelHeld].filter((id) => !onSale.includes(id));
  return [...new Set([...onSale, ...extra])].map((id) => ({
    id, price: INTEL.find((card) => card.id === id)?.price ?? 0, owned: owns(op, node, id),
  }));
}

/** Charge RP and attach the card; every refusal is an Error the briefing can show. Returns the log line. */
export function buyIntel(profile: ProfileState, nodeId: string, intel: IntelId): string {
  const op = activeOperation(profile);
  if (op.deployed) throw new Error('A ranger is already in the field: intel can no longer be bought.');
  const node = op.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`No such node: ${nodeId}.`);
  if (!node.params || !flyable(node)) throw new Error('Intel is only sold for missions, not for support nodes.');
  if (node.state !== 'open') throw new Error('Intel is only sold for a mission the company can reach now.');
  const card: IntelInfo | undefined = INTEL.find((c) => c.id === intel);
  if (!card || !intelOffers(node).includes(card.id)) throw new Error('That intel is not on offer for this mission.');
  if (owns(op, node, card.id)) throw new Error(`${card.name} is already in hand for this mission.`);
  if (op.rp < card.price) throw new Error(`${card.name} costs ${card.price} RP; the company has ${op.rp}.`);
  op.rp -= card.price;
  node.params.intel.push(card.id);
  const line = `Intel bought: ${card.name} (-${card.price} RP).`;
  op.log.push(line);
  return line;
}
